import { Box } from '@chakra-ui/react';
import createMentionPlugin from '@draft-js-plugins/mention';
import { MentionSuggestionsPubProps } from '@draft-js-plugins/mention/lib/MentionSuggestions/MentionSuggestions';
import * as React from 'react';

import { Mention } from './components/Mention';

export const createEditorMentionPlugin = (): ReturnType<
  typeof createMentionPlugin
> =>
  createMentionPlugin({
    entityMutability: 'IMMUTABLE',
    supportWhitespace: true,
    mentionComponent: Mention,
    theme: {
      mentionSuggestions: 'mentionSuggestions',
      mentionSuggestionsPopup: 'mentionSuggestionsPopup',
      mentionSuggestionsPopupVisible: 'mentionSuggestionsPopupVisible',
      mentionSuggestionsEntry: 'mentionSuggestionsEntry',
      mentionSuggestionsEntryFocused: 'mentionSuggestionsEntryFocused',
    },
  });

const entryShared = {
  paddingTop: '2',
  paddingBottom: '2',
  paddingRight: '2.5',
  paddingLeft: '2.5',
  transition: 'background-color 0.4s cubic-bezier(.27,1.27,.48,.56)',
  borderRadius: 'lg',
};

const getRangeRect = (range: Range | null): DOMRect | null => {
  if (!range) {
    return null;
  }

  const rects = range.getClientRects();
  if (rects.length > 0) {
    return rects[0];
  }

  const rect = range.getBoundingClientRect();
  if (rect.top || rect.left || rect.bottom) {
    return rect;
  }

  return null;
};

const captureAtRange = (): Range | null => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  return selection.getRangeAt(0).cloneRange();
};

export const MentionSuggestionsWrapper: React.FC<
  MentionSuggestionsPubProps & {
    MentionSuggestions: React.ComponentType<MentionSuggestionsPubProps>;
  }
> = ({ MentionSuggestions, open, onOpenChange, suggestions, ...props }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const atRangeRef = React.useRef<Range | null>(null);

  React.useLayoutEffect(() => {
    if (!open || suggestions.length === 0) {
      atRangeRef.current = null;
      return;
    }

    const nextRange = captureAtRange();
    if (nextRange) {
      atRangeRef.current = nextRange;
    }

    const applyPosition = (): void => {
      const popup = containerRef.current?.querySelector(
        '.mentionSuggestions'
      ) as HTMLElement | null;
      const rect = getRangeRect(atRangeRef.current);
      if (!popup || !rect) {
        return;
      }

      popup.style.position = 'fixed';
      popup.style.top = `${rect.bottom + 4}px`;
      popup.style.left = `${rect.left}px`;
      popup.style.transform = 'scale(1)';
    };

    applyPosition();
    const frame = window.requestAnimationFrame(applyPosition);
    window.addEventListener('scroll', applyPosition, true);
    window.addEventListener('resize', applyPosition);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', applyPosition, true);
      window.removeEventListener('resize', applyPosition);
    };
  }, [open, suggestions]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const closeIfOutside = (event: MouseEvent): void => {
      const target = event.target as Node | null;
      if (!target) {
        return;
      }

      const popup = containerRef.current?.querySelector(
        '.mentionSuggestions'
      );
      if (popup?.contains(target)) {
        return;
      }

      onOpenChange(false);
    };

    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onOpenChange(false);
      }
    };

    document.addEventListener('mousedown', closeIfOutside, true);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeIfOutside, true);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open, onOpenChange]);

  return (
    <Box
      ref={containerRef}
      sx={{
        '.mentionSuggestions': {
          position: 'absolute',
          minWidth: '56',
          maxWidth: '96',
          background: 'white',
          borderRadius: 'lg',
          boxShadow: 'lg',
          cursor: 'pointer',
          padding: '1',
          zIndex: '2',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          transform: 'scale(0)',
        },
        '.mentionSuggestionsPopup': {
          opacity: '0',
          transition: 'opacity 0.25s cubic-bezier(0.3, 1.2, 0.2, 1)',
          borderRadius: 'lg',
        },
        '.mentionSuggestionsPopupVisible': {
          opacity: '1',
        },
        '.mentionSuggestionsEntry': {
          ...entryShared,
        },
        '.mentionSuggestionsEntry:active': {
          backgroundColor: 'gray.100',
        },
        '.mentionSuggestionsEntryFocused': {
          ...entryShared,
          backgroundColor: 'gray.100',
        },
        '.mentionSuggestionsEntryFocused:active': {
          backgroundColor: 'gray.100',
        },
      }}
    >
      <MentionSuggestions
        {...props}
        open={open}
        onOpenChange={onOpenChange}
        suggestions={suggestions}
      />
    </Box>
  );
};
